import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import queryString, { ParsedQuery } from 'query-string'
import FileSaver from 'file-saver'
import log from 'loglevel'
import Papa from 'papaparse'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useScopedColumnHidden } from 'src/context/ColumnHideListContext'
import { useHasVendorTprmQuestionnaire } from 'src/context/Gatekeeper'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import { EVALUATE_QUESTIONNAIRE_PLAYBOOK_NAME } from 'src/components/Reusable/Questionnaire/questionnairePlaybooks'
import {
  CreateVendorRequest,
  CreateVendorsRequest,
  DeleteVendorRequest,
  GetVendorRequest,
  AddVendorUserRequest,
  ListVendorsRequest,
  ListVendorsResponse,
  ListVendorUsersRequest,
  ListVendorUsersResponse,
  RemoveVendorUserRequest,
  UpdateVendorUserRequest,
  UpdateVendorRequest,
  UpdateVendorsRequest,
  VendorRecord,
  VendorStatusItem,
  VendorStatuses,
  ATTESTATION_STATUS,
} from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import {
  RISK_LINK_TYPE,
  RiskLink,
} from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  AddAttestationRequest,
  AddAttestationsRequest,
  AttestationRecord,
  Attestations,
  DeleteAttestationRequest,
  ListAttestationsRequest,
  UpdateAttestationRequest,
} from '@trustero/trustero-api-web/lib/vendormanagement/attestation_pb'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import { AttestationPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/attestation_grpc_web_pb'
import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { QuestionnaireServicePromiseClient } from '@trustero/trustero-api-web/lib/tprmquestionnaire/tprmquestionnaire_grpc_web_pb'
import {
  GetLatestQuestionnaireVersionRequest,
  GetLatestQuestionnaireVersionResponse,
  AddVendorQuestionnaireRequest,
  AnswerInternally,
  GetVendorQuestionnaireRequest,
  GetVendorQuestionnaireResponse,
  GetVendorQuestionnaireVersionRequest,
  GetVendorQuestionnaireVersionResponse,
  DownloadVendorQuestionnaireAttachmentRequest,
  ListVendorQuestionnaireAttachmentsRequest,
  ListVendorQuestionnaireAttachmentsResponse,
  ListVendorQuestionnaireVersionsRequest,
  ListVendorQuestionnaireVersionsResponse,
  ListVendorQuestionnairesRequest,
  ListVendorQuestionnairesResponse,
  RemoveVendorQuestionnaireRequest,
  SendToVendorUser,
  GRADE_BAND,
  SetAnswerConfidenceRequest,
  SetVendorQuestionnaireStatusRequest,
  UpdateVendorQuestionnaireRequest,
  DraftVendorQuestionnaireSend,
  ListVendorQuestionnaireReminderRecipientOptionsRequest,
  UpdateVendorQuestionnaireRemindersRequest,
} from '@trustero/trustero-api-web/lib/tprmquestionnaire/tprmquestionnaire_pb'
import { VendorQuestionnaireReceivedServicePromiseClient } from '@trustero/trustero-api-web/lib/tprmquestionnaire/vendor_questionnaire_received_grpc_web_pb'
import {
  DueDateReminderRecipientOptions,
  DueDateReminders,
} from '@trustero/trustero-api-web/lib/common/due_date_reminder_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { Playbook } from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useGrpcRevalidateByMethod } from 'src/components'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { useConfirmationContext } from 'src/Confirmation'
import { useFetchAttestationBody } from 'src/components/async/document/useDocument'
import { useThrobberContext } from 'src/Throbber/ThrobberContext'
import { capitalizeFirstLetter } from 'src/Utils/globalHelpers'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { extension, resolveUploadMime } from 'src/Utils/mime'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useConfirmationModal } from 'src/components/ModalForms'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { useVendorEvaluationCriteria } from 'src/components/async/model/evaluationCriteria/useVendorEvaluationCriteria'
import {
  useCreateVendorEvaluationCriteria,
  useDeleteVendorEvaluationCriteria,
  useUpdateVendorEvaluationCriteria,
} from 'src/components/async/model/evaluationCriteria/useVendorEvaluationCriteriaMutations'
import { useToggleLinks } from '../Risks/risks.hooks'
import {
  RISK_LEVEL_TO_LABEL,
  AttestationStatusLabels,
  NO_VENDOR_STATUS_LABEL,
  vendorStateLabel,
} from './vendors.constants'
import { applyVendorsFilters, applyVendorsSort } from './vendors.helpers'
import {
  isOpenEvaluation,
  OPEN_EVALUATION_POLL_MS,
} from './evaluations/vendorEvaluations.hooks'

const ATTESTATION_STATUS_TO_LABEL: Record<
  ATTESTATION_STATUS,
  AttestationStatusLabels
> = {
  [ATTESTATION_STATUS.NONE]: AttestationStatusLabels.NONE,
  [ATTESTATION_STATUS.STALE]: AttestationStatusLabels.STALE,
  [ATTESTATION_STATUS.FRESH]: AttestationStatusLabels.FRESH,
}

export const useDownloadVendorCSV = (): ((vendors: VendorRecord[]) => void) => {
  // Status is exported by name, not id, so the file reads (and matches what the
  // upload's Status column expects).
  const { statusById } = useVendorStatusesById()

  return useCallback(
    (vendors: VendorRecord[]) => {
      const data = vendors.map((vendor) => ({
        Name: vendor.getName(),
        'Risk Tier': RISK_LEVEL_TO_LABEL[vendor.getRiskLevel()],
        Attestation: ATTESTATION_STATUS_TO_LABEL[vendor.getAttestationStatus()],
        'Related Risks': vendor.getRisksCount(),
        Owner: vendor.getOwnerEmail(),
        Assignees: vendor.getAssigneeEmailsList().join(','),
        Status:
          statusById.get(vendor.getCustomStatusId())?.getName() ??
          NO_VENDOR_STATUS_LABEL,
        State: vendorStateLabel(vendor.getActive()),
      }))

      const csv = Papa.unparse(data)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      FileSaver.saveAs(blob, 'vendor-management.csv')
    },
    [statusById],
  )
}

export const useInvalidateVendorsCache = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    try {
      await Promise.all([
        mutateFunc(VendorPromiseClient.prototype.listVendors),
        mutateFunc(VendorPromiseClient.prototype.getVendor),
        mutateFunc(AttestationPromiseClient.prototype.listAttestations),
        mutateFunc(RequestPromiseClient.prototype.listRequestAttestations),
        mutateFunc(VendorPromiseClient.prototype.listVendorUsers),
      ])
    } catch (error) {
      log.error('Error when invalidating vendors cache', error)
    }
  }, [mutateFunc])
}

export const useVendorsRequest = (): ListVendorsRequest => {
  const location = useLocation()

  return useMemo(() => {
    const req = new ListVendorsRequest()
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    // Apply all filters for each parameter in URL
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyVendorsFilters(req, queryParams, filterType),
    )
    applyVendorsSort(req, queryParams)
    return req
  }, [location.search])
}

export const useVendor = (
  vendorId: string,
  shouldFetch = true,
): GrpcResponse<VendorRecord> => {
  const request = new GetVendorRequest().setId(vendorId)
  const { response } = useSwrImmutableGrpc(
    VendorPromiseClient.prototype.getVendor,
    request,
    shouldFetch,
    {
      // While the vendor's evaluation is open, its stored status and summary
      // move as the agent works: poll, as the Evaluations tab does. Inline so
      // SWR re-arms the interval as the status changes.
      refreshInterval: (latest?: VendorRecord) =>
        latest && isOpenEvaluation(latest.getEvaluationStatus())
          ? OPEN_EVALUATION_POLL_MS
          : 0,
    },
  )
  return NewGrpcResponse(response)
}

export const useVendors = (
  req: ListVendorsRequest = new ListVendorsRequest(),
  shouldFetch = true,
): GrpcResponse<ListVendorsResponse> => {
  const { response } = useSwrImmutableGrpc(
    VendorPromiseClient.prototype.listVendors,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// The account's vendor status list, keyed by id, for rendering a vendor's
// status pill. Status colours are per-account data rather than a static map, so
// the grid has to look them up rather than deriving them from an enum.
export const useVendorStatusesById = (): {
  statusById: Map<string, VendorStatusItem>
  isLoading: boolean
} => {
  const { response } = useSwrImmutableGrpc(
    VendorPromiseClient.prototype.listVendorStatuses,
    new Empty(),
  )
  const { data, isLoading }: GrpcResponse<VendorStatuses> =
    NewGrpcResponse(response)

  const statusById = useMemo(() => {
    const map = new Map<string, VendorStatusItem>()
    data?.getItemsList().forEach((status) => map.set(status.getId(), status))
    return map
  }, [data])

  return { statusById, isLoading }
}

// The status filter's options, in the account's own lifecycle order. Values are
// ids rather than names so a rename does not orphan a saved filter URL.
export const useVendorStatusFilterOptions = (): {
  value: string
  label: string
}[] => {
  const { statusById } = useVendorStatusesById()

  return useMemo(
    () =>
      Array.from(statusById.values())
        .sort((a, b) => a.getOrder() - b.getOrder())
        .map((status) => ({
          value: status.getId(),
          label: status.getName(),
        })),
    [statusById],
  )
}

// The questionnaire template hooks live in
// questionnaireTemplates/questionnaireTemplates.hooks.tsx, with the rest of the
// authoring surface.

export const useBulkCreateVendors = (): ((
  requests: CreateVendorRequest[],
) => Promise<void>) => {
  const mutate = useInvalidateVendorsCache()
  const vendorClient = useAuthorizedGrpcClient(VendorPromiseClient)

  return async (requests: CreateVendorRequest[]) => {
    const request = new CreateVendorsRequest().setRequestsList(requests)
    await vendorClient.createVendors(request)
    await mutate()
  }
}

export const useUpdateVendors = (): ((
  requests: UpdateVendorRequest[],
) => Promise<void>) => {
  const mutate = useInvalidateVendorsCache()
  const vendorClient = useAuthorizedGrpcClient(VendorPromiseClient)

  return async (requests: UpdateVendorRequest[]) => {
    const updates = new UpdateVendorsRequest().setRequestsList(requests)
    await vendorClient.updateVendors(updates)
    await mutate()
  }
}

export const useToggleVendorReviewed = (
  vendor: VendorRecord,
): {
  isReviewed: boolean
  toggleReviewed: (e?: React.MouseEvent) => Promise<void>
} => {
  const updateVendors = useUpdateVendors()
  const lastReviewedAt = vendor.getLastReviewedAt()
  const isReviewed = lastReviewedAt
    ? lastReviewedAt.getSeconds() > 0 || lastReviewedAt.getNanos() > 0
    : false

  const toggleReviewed = useCallback(
    async (e?: React.MouseEvent) => {
      try {
        e?.preventDefault()
        const update = new UpdateVendorRequest()
          .setId(vendor.getId())
          .setLastReviewedAt(
            isReviewed
              ? new Timestamp().setSeconds(0)
              : Timestamp.fromDate(new Date()),
          )

        await updateVendors([update])
      } catch (err) {
        log.error('Failed to toggle vendor reviewed status', err)
        showInfoToast('Failed to update vendor review status')
      }
    },
    [vendor, updateVendors, isReviewed],
  )

  return { isReviewed, toggleReviewed }
}

/**
 * Custom hook to delete a vendor.
 *
 * @returns A function that deletes a vendor.
 */
export const useDeleteVendor = (): ((id: string) => Promise<void>) => {
  const { setConfirmationState } = useConfirmationContext()
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const mutate = useInvalidateVendorsCache()
  const { startThrobber, stopThrobber } = useThrobberContext()

  return async (id: string) => {
    setConfirmationState({
      isShown: true,
      size: 'lg',
      title: 'Are you sure you want to delete this vendor?',
      body: 'This will delete any attestations and links to risks.',
      confirmText: 'Delete Vendor',
      onConfirmCB: async () => {
        try {
          startThrobber()
          await client.deleteVendor(new DeleteVendorRequest().setId(id))
        } catch (err) {
          log.error('Error deleting vendor', err)
          setConfirmationState({
            isShown: false,
            title: '',
            body: '',
            confirmText: '',
            onConfirmCB: () => null,
          })
          showInfoToast(
            'There was an error deleting the vendor. Please try again.',
          )
        } finally {
          await mutate()
          stopThrobber()
        }
      },
    })
  }
}

export const useVendorUsers = (
  vendorId: string,
  shouldFetch = true,
): GrpcResponse<ListVendorUsersResponse> => {
  const request = new ListVendorUsersRequest().setVendorId(vendorId)
  const { response } = useSwrImmutableGrpc(
    VendorPromiseClient.prototype.listVendorUsers,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Additional Evaluation Guidance is stored through the per-vendor evaluation
// criteria API as one item with this fixed title; users only see its body.
export const ADDITIONAL_GUIDANCE_TITLE = 'Additional Evaluation Guidance'

export const useVendorAdditionalGuidance = (
  vendorId: string,
  shouldFetch = true,
): {
  guidance: string
  loading: boolean
  saveGuidance: (body: string) => Promise<void>
} => {
  const { data, loading } = useVendorEvaluationCriteria(vendorId, shouldFetch)
  const createCriteria = useCreateVendorEvaluationCriteria()
  const updateCriteria = useUpdateVendorEvaluationCriteria()
  const deleteCriteria = useDeleteVendorEvaluationCriteria()
  const item = data
    ?.getItemsList()
    .find((criteria) => criteria.getTitle() === ADDITIONAL_GUIDANCE_TITLE)
  const itemId = item?.getId()
  const isLoaded = !!data

  const saveGuidance = useCallback(
    async (value: string) => {
      // A failed list load looks like "no item yet"; creating then would
      // duplicate an item the vendor may already have.
      if (!isLoaded) throw new Error('Additional guidance has not loaded')
      const body = value.trim()
      if (!itemId) {
        if (body) {
          await createCriteria({
            vendorId,
            title: ADDITIONAL_GUIDANCE_TITLE,
            body,
          })
        }
      } else if (body) {
        await updateCriteria({ vendorId, evaluationCriteriaId: itemId, body })
      } else {
        // Clearing the field removes the item, so evaluations never see an
        // empty criterion.
        await deleteCriteria({ vendorId, evaluationCriteriaId: itemId })
      }
    },
    [
      vendorId,
      isLoaded,
      itemId,
      createCriteria,
      updateCriteria,
      deleteCriteria,
    ],
  )

  return { guidance: item?.getBody() ?? '', loading, saveGuidance }
}

// Adding a vendor contact is bookkeeping only -- no email, no portal access.
// The vendor only hears about anything when a questionnaire is sent to them
// (see useAddVendorQuestionnaire's send intent).
export const useAddVendorUser = (): ((
  vendorId: string,
  name: string,
  email: string,
  title: string,
  notes: string,
) => Promise<string>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return useCallback(
    async (
      vendorId: string,
      name: string,
      email: string,
      title: string,
      notes: string,
    ) => {
      const req = new AddVendorUserRequest()
        .setVendorId(vendorId)
        .setName(name)
        .setEmail(email)
        .setTitle(title)
        .setNotes(notes)
      const record = await client.addVendorUser(req)
      await mutate()
      showInfoToast('Vendor user added.')
      return record.getId()
    },
    [client, mutate],
  )
}

export const useUpdateVendorUser = (): ((
  vendorUserId: string,
  name: string,
  email: string,
  title: string,
  notes: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return useCallback(
    async (
      vendorUserId: string,
      name: string,
      email: string,
      title: string,
      notes: string,
    ) => {
      const req = new UpdateVendorUserRequest()
        .setVendorUserId(vendorUserId)
        .setName(name)
        .setEmail(email)
        .setTitle(title)
        .setNotes(notes)
      await client.updateVendorUser(req)
      await mutate()
      showInfoToast('Vendor user updated.')
    },
    [client, mutate],
  )
}

export const useRemoveVendorUser = (): ((
  vendorUserId: string,
  vendorUserName: string,
) => void) => {
  const { setConfirmationState } = useConfirmationContext()
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return (vendorUserId: string, vendorUserName: string) => {
    setConfirmationState({
      isShown: true,
      size: 'lg',
      title: `Remove ${vendorUserName} from the vendor portal?`,
      body: 'They will no longer have access to the vendor portal once it is available.',
      confirmText: 'Remove Vendor User',
      onConfirmCB: async () => {
        try {
          await client.removeVendorUser(
            new RemoveVendorUserRequest().setVendorUserId(vendorUserId),
          )
        } catch (err) {
          log.error('Error removing vendor user', err)
          showInfoToast(
            'There was an error removing this vendor user. Please try again.',
          )
        } finally {
          await mutate()
        }
      },
    })
  }
}

export const useAttestations = (
  vendorId: string,
  shouldFetch = true,
): GrpcResponse<Attestations> => {
  const request = new ListAttestationsRequest().setVendorId(vendorId)
  const { response } = useSwrImmutableGrpc(
    AttestationPromiseClient.prototype.listAttestations,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useAddAttestation = (): (({
  description,
  reportType,
  expirationDate,
  vendorId,
  vendorName,
  file,
}: {
  description: string
  reportType: string
  expirationDate: Date
  vendorId: string
  vendorName: string
  file: File
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttestationPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return async ({
    description,
    reportType,
    expirationDate,
    vendorId,
    vendorName,
    file,
  }) => {
    const attestation = new AttestationRecord()
      .setDescription(description)
      .setReportType(reportType)
      .setExpirationDate(Timestamp.fromDate(expirationDate))
      .setVendorId(vendorId)
      .setMime(resolveUploadMime(file))
    const attestationReq = new AddAttestationRequest()
      .setAttestation(attestation)
      .setBody(new Uint8Array(await new Response(file).arrayBuffer()))
      .setVendorName(vendorName)
    const req = new AddAttestationsRequest().setRequestsList([attestationReq])
    await client.addAttestations(req)
    await mutate()
  }
}

/**
 * Custom hook to delete an attestation.
 *
 * @returns A function that deletes an attestation.
 */
export const useDeleteAttestation = (): ((id: string) => Promise<void>) => {
  const { setConfirmationState } = useConfirmationContext()
  const client = useAuthorizedGrpcClient(AttestationPromiseClient)
  const mutate = useInvalidateVendorsCache()
  const { startThrobber, stopThrobber } = useThrobberContext()

  return async (id: string) => {
    setConfirmationState({
      isShown: true,
      size: 'lg',
      title: 'Delete Attestation?',
      body: 'This will remove the attestation from this vendor.',
      confirmText: 'Delete',
      onConfirmCB: async () => {
        try {
          startThrobber()
          await client.deleteAttestation(
            new DeleteAttestationRequest().setId(id),
          )
        } catch (err) {
          log.error('Error deleting attestation', err)
          setConfirmationState({
            isShown: false,
            title: '',
            body: '',
            confirmText: '',
            onConfirmCB: () => null,
          })
          showInfoToast(
            'There was an error deleting the attestation. Please try again.',
          )
        } finally {
          await mutate()
          stopThrobber()
        }
      },
    })
  }
}

// Attestations are no longer PDF-only, so the download names and types the
// file from the record's own mime. `mime` defaults to PDF for the records
// written before other types were accepted, which carry no mime.
export const useDownloadAttestation = (
  contentId: string,
  description: string,
  mime: string = MIME_TYPE.APPLICATION_PDF,
): (() => Promise<void>) => {
  const attestationMime = mime || MIME_TYPE.APPLICATION_PDF
  const fetchBody = useFetchAttestationBody(contentId, attestationMime)

  return async () => {
    try {
      const body = await fetchBody()
      // A text/* mime comes back as a string; everything else as bytes.
      if (typeof body !== 'string' && !(body instanceof Uint8Array)) {
        showInfoToast(
          'There was a problem downloading the attestation. Please try again.',
        )
        return
      }
      const blob = new Blob([body], {
        type: attestationMime,
      })
      const ext = extension(attestationMime)
      FileSaver.saveAs(blob, ext ? `${description}.${ext}` : description)
    } catch (err) {
      log.error('Error downloading attestation', err)
      showInfoToast(
        'There was an error downloading the attestation. Please try again.',
      )
    }
  }
}

export const useUnlinkVendorRiskModal = (
  vendorId: string,
  vendorName: string,
  riskId: string,
  riskCustomId: string,
  riskName: string,
  subjectType: MODEL_TYPE = MODEL_TYPE.VENDOR,
): (() => void) => {
  const unlinkFunc = useToggleLinks()
  const mutate = useInvalidateVendorsCache()
  const isVendorSubject = subjectType === MODEL_TYPE.VENDOR
  const subject = isVendorSubject ? 'vendor' : 'risk'
  const attachment = isVendorSubject ? 'risk' : 'vendor'
  const subjectId = isVendorSubject ? vendorId : riskId
  const riskText = `${riskCustomId} ${riskName}`
  const title = isVendorSubject
    ? `Unlink Risk from ${vendorName}?`
    : `Unlink ${vendorName} from this Risk?`
  const body = isVendorSubject
    ? `This will remove ${riskText}`
    : `This will remove ${vendorName} from ${riskText}`

  const unlinkVendor = useCallback(async () => {
    try {
      await unlinkFunc({
        type: RISK_LINK_TYPE.UNLINK,
        links: [
          new RiskLink()
            .setId(vendorId)
            .setModelType(MODEL_TYPE.VENDOR)
            .setRiskId(riskId),
        ],
      })
      await mutate()
    } catch (err) {
      log.error(
        `Error unlinking ${attachment} from ${subject} id ${subjectId}`,
        err,
      )
      showInfoToast(
        `There was an error unlinking the ${attachment} from the ${subject}. Please try again.`,
      )
    }
  }, [unlinkFunc, attachment, subject, subjectId, riskId, vendorId, mutate])

  const confirmationModalProps = {
    title: title,
    body: body,
    confirmText: `Unlink ${capitalizeFirstLetter(attachment)}`,
    onConfirmCB: unlinkVendor,
  }

  return useConfirmationModal(confirmationModalProps)
}

export type UpdateAttestation = {
  id: string
  description: string
  reportType: string
  expirationDate: Date
}

export const useUpdateAttestation = (): ((
  attestation: UpdateAttestation,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(AttestationPromiseClient)
  const mutate = useInvalidateVendorsCache()

  return async ({ id, description, reportType, expirationDate }) => {
    const attestation = new AttestationRecord()
      .setDescription(description)
      .setReportType(reportType)
      .setExpirationDate(Timestamp.fromDate(expirationDate))
      .setId(id)
    const req = new UpdateAttestationRequest().setAttestation(attestation)
    await client.updateAttestation(req)
    await mutate()
  }
}

export const useSearchableVendors = (
  vendors: VendorRecord[],
): SearchableData<VendorRecord>[] => {
  return useMemo(() => {
    return prepareSearchableData(
      vendors,
      (vendor: VendorRecord) => vendor.getId(),
      (vendor: VendorRecord) => {
        const name = vendor.getName()
        const riskTier = vendor.getRiskLevel()
        const riskTierText = RISK_LEVEL_TO_LABEL[riskTier]
        const relatedRisk = vendor.getRisksCount().toString()
        const owner = vendor.getOwnerEmail()

        return [name, riskTierText, relatedRisk, owner]
      },
    )
  }, [vendors])
}

// Preview of one sent questionnaire: the vendor's latest submission if they've
// submitted, otherwise the questions as sent with no answers. Keyed by the
// questionnaire request id, so the same hook serves the preview action from the
// vendor show page, the vendors index page, and the vendor portal.
export const useLatestQuestionnaireVersion = (
  questionnaireRequestId: string,
  shouldFetch = true,
): GrpcResponse<GetLatestQuestionnaireVersionResponse> => {
  const req =
    new GetLatestQuestionnaireVersionRequest().setQuestionnaireRequestId(
      questionnaireRequestId,
    )

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.getLatestQuestionnaireVersion,
    req,
    shouldFetch && !!questionnaireRequestId,
  )
  return NewGrpcResponse(response)
}

// The questionnaires this account has sent. vendorId scopes it to one vendor
// (the vendor show page tab); omitting it lists every vendor's (the vendors
// index page tab).
export const useVendorQuestionnaires = (
  vendorId?: string,
  shouldFetch = true,
): GrpcResponse<ListVendorQuestionnairesResponse> => {
  const req = new ListVendorQuestionnairesRequest()
  if (vendorId) {
    req.setVendorId(vendorId)
  }

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.listVendorQuestionnaires,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useVendorQuestionnaire = (
  questionnaireRequestId: string,
  shouldFetch = true,
): GrpcResponse<GetVendorQuestionnaireResponse> => {
  const req = new GetVendorQuestionnaireRequest().setQuestionnaireRequestId(
    questionnaireRequestId,
  )

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.getVendorQuestionnaire,
    req,
    shouldFetch && !!questionnaireRequestId,
  )
  return NewGrpcResponse(response)
}

export const useInvalidateVendorQuestionnaires = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    try {
      await Promise.all([
        mutateFunc(
          QuestionnaireServicePromiseClient.prototype.listVendorQuestionnaires,
        ),
        mutateFunc(
          QuestionnaireServicePromiseClient.prototype.getVendorQuestionnaire,
        ),
      ])
    } catch (error) {
      log.error('Error when invalidating vendor questionnaires', error)
    }
  }, [mutateFunc])
}

// What the questionnaire is for, which the server requires: it is either sent
// to a vendor contact or claimed for internal answering. A union rather than
// optional fields so the choice cannot be left out -- an absent intent is the
// stranded-questionnaire state this call exists to make unreachable.
export type AddVendorQuestionnaireIntent =
  | {
      kind: 'send'
      vendorUserId: string
      dueDateMs?: number
      dueDateReminders?: DueDateReminders
    }
  | { kind: 'answerInternally' }

// One call: the questionnaire is added and its fate decided together, so a
// failure leaves nothing behind. Returns the new questionnaire's id, which is
// also what to open in order to answer it.
export const useAddVendorQuestionnaire = (): ((args: {
  templateId: string
  vendorId: string
  description?: string
  intent: AddVendorQuestionnaireIntent
}) => Promise<string>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)
  const mutateQuestionnaires = useInvalidateVendorQuestionnaires()
  const mutateVendors = useInvalidateVendorsCache()

  return useCallback(
    async ({ templateId, vendorId, description, intent }) => {
      const req = new AddVendorQuestionnaireRequest()
        .setQuestionnaireTemplateId(templateId)
        .setVendorId(vendorId)
        .setDescription(description ?? '')
      if (intent.kind === 'send') {
        const send = new SendToVendorUser().setVendorUserId(intent.vendorUserId)
        if (intent.dueDateMs !== undefined) {
          send.setDueDate(intent.dueDateMs)
        }
        if (intent.dueDateReminders) {
          send.setDueDateReminders(intent.dueDateReminders)
        }
        req.setSend(send)
      } else {
        req.setAnswerInternally(new AnswerInternally())
      }
      const resp = await client.addVendorQuestionnaire(req)
      // A send also creates a Request against the vendor, so on that path the
      // vendor caches are stale too, not just the questionnaire list.
      await Promise.all(
        intent.kind === 'send'
          ? [mutateQuestionnaires(), mutateVendors()]
          : [mutateQuestionnaires()],
      )
      showInfoToast(
        intent.kind === 'send' ? 'Questionnaire sent.' : 'Questionnaire added.',
      )
      return resp.getQuestionnaireRequestId()
    },
    [client, mutateQuestionnaires, mutateVendors],
  )
}

// Editing is refused server-side once the questionnaire has been sent, so a
// failure here is a real error rather than something to swallow -- the caller
// surfaces it.
export const useUpdateVendorQuestionnaire = (): ((args: {
  questionnaireRequestId: string
  description?: string
  // 0 clears the due date; undefined leaves it untouched.
  dueDateMs?: number
  // '' clears the assignment, the way the picker's Unassigned option travels;
  // undefined leaves it untouched.
  assigneeUserId?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)
  const mutate = useInvalidateVendorQuestionnaires()

  return useCallback(
    async ({
      questionnaireRequestId,
      description,
      dueDateMs,
      assigneeUserId,
    }) => {
      const req =
        new UpdateVendorQuestionnaireRequest().setQuestionnaireRequestId(
          questionnaireRequestId,
        )
      if (description !== undefined) {
        req.setDescription(description)
      }
      if (dueDateMs !== undefined) {
        req.setDueDate(dueDateMs)
      }
      if (assigneeUserId !== undefined) {
        req.setAssigneeUserId(assigneeUserId)
      }
      await client.updateVendorQuestionnaire(req)
      await mutate()
    },
    [client, mutate],
  )
}

export type VendorQuestionnaireReminderTarget =
  | { questionnaireRequestId: string }
  | { vendorId: string; vendorUserId: string } // a send being prepared

export const useVendorQuestionnaireReminderRecipientOptions = (
  target: VendorQuestionnaireReminderTarget,
  shouldFetch = true,
): GrpcResponse<DueDateReminderRecipientOptions> => {
  const req = new ListVendorQuestionnaireReminderRecipientOptionsRequest()
  if ('questionnaireRequestId' in target) {
    req.setQuestionnaireRequestId(target.questionnaireRequestId)
  } else {
    req.setDraftSend(
      new DraftVendorQuestionnaireSend()
        .setVendorId(target.vendorId)
        .setVendorUserId(target.vendorUserId),
    )
  }
  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype
      .listVendorQuestionnaireReminderRecipientOptions,
    req,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Reminders stay editable after sending, unlike the questionnaire itself. A
// refusal is left to the caller to show.
export const useUpdateVendorQuestionnaireReminders = (): ((args: {
  questionnaireRequestId: string
  reminders: DueDateReminders
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)
  const mutate = useInvalidateVendorQuestionnaires()
  const revalidate = useGrpcRevalidateByMethod()

  return useCallback(
    async ({ questionnaireRequestId, reminders }) => {
      await client.updateVendorQuestionnaireReminders(
        new UpdateVendorQuestionnaireRemindersRequest()
          .setQuestionnaireRequestId(questionnaireRequestId)
          .setDueDateReminders(reminders),
      )
      await Promise.all([
        mutate(),
        revalidate(
          QuestionnaireServicePromiseClient.prototype
            .listVendorQuestionnaireReminderRecipientOptions,
        ),
      ])
    },
    [client, mutate, revalidate],
  )
}

// The user-set half of the lifecycle: freezing a questionnaire (Complete or
// Cancelled) and reopening a frozen one. The caller sends the target status
// itself -- an unreachable one is refused server-side, which is the point: a
// client showing "Reopen" should fail loudly if its idea of where that leads
// has drifted. Errors are left to the caller, which keeps its form open on one.
export const useSetVendorQuestionnaireStatus = (): ((args: {
  questionnaireRequestId: string
  status: string
  note?: string
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)
  const mutate = useInvalidateVendorQuestionnaires()

  return useCallback(
    async ({ questionnaireRequestId, status, note }) => {
      const req = new SetVendorQuestionnaireStatusRequest()
        .setQuestionnaireRequestId(questionnaireRequestId)
        .setStatus(status)
        .setNote(note ?? '')
      await client.setVendorQuestionnaireStatus(req)
      await mutate()
      showInfoToast('Questionnaire status updated.')
    },
    [client, mutate],
  )
}

// Evaluate Questionnaire Answers needs a questionnaire to evaluate, so a vendor page
// with none leaves it out of the copilot's library. vendorId scopes it to one
// vendor; omitted, any vendor's questionnaire counts. Hidden until the list
// loads, rather than offered and then withdrawn.
export const useEvaluatePlaybookFilter = (
  vendorId?: string,
): ((playbook: Playbook) => boolean) => {
  const { data } = useVendorQuestionnaires(vendorId)
  const hasQuestionnaires = (data?.getRequestsList().length ?? 0) > 0
  return useCallback(
    (playbook: Playbook) =>
      hasQuestionnaires ||
      playbook.getName() !== EVALUATE_QUESTIONNAIRE_PLAYBOOK_NAME,
    [hasQuestionnaires],
  )
}

// A reviewer's own confidence in one answer, shown in place of the grader's;
// UNSPECIFIED goes back to the grader's. Refreshes both reads that carry it: a
// vendor's latest submission, and the answering view of one answered in-house.
export const useSetAnswerConfidence = (): ((args: {
  questionnaireRequestId: string
  questionId: string
  confidence: GRADE_BAND
}) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(
    async ({ questionnaireRequestId, questionId, confidence }) => {
      try {
        await client.setAnswerConfidence(
          new SetAnswerConfidenceRequest()
            .setQuestionnaireRequestId(questionnaireRequestId)
            .setQuestionId(questionId)
            .setConfidence(confidence),
        )
        await Promise.all([
          mutateFunc(
            QuestionnaireServicePromiseClient.prototype
              .getLatestQuestionnaireVersion,
          ),
          mutateFunc(
            VendorQuestionnaireReceivedServicePromiseClient.prototype
              .getReceivedVendorQuestionnaire,
          ),
        ])
      } catch (error) {
        log.error('Error setting answer confidence', error)
        showInfoToast(
          'There was an error changing the confidence. Please try again.',
        )
      }
    },
    [client, mutateFunc],
  )
}

// The customer's copies of what the answering side submitted -- one per submit,
// newest first (see table.VersionQuestionnaireOnSubmit).
// useVendorQuestionnaireAttachments fetches the attachments the vendor
// submitted, for the source show page's Attachments tab (AP-19838). Reads this
// account's mirrored copy of the latest submitted version, so it is an ordinary
// same-account read despite describing the vendor's files.
export const useVendorQuestionnaireAttachments = (
  questionnaireRequestId: string,
  shouldFetch = true,
): GrpcResponse<ListVendorQuestionnaireAttachmentsResponse> => {
  const req =
    new ListVendorQuestionnaireAttachmentsRequest().setQuestionnaireRequestId(
      questionnaireRequestId,
    )

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype
      .listVendorQuestionnaireAttachments,
    req,
    shouldFetch && !!questionnaireRequestId,
  )
  return NewGrpcResponse(response)
}

// useDownloadVendorQuestionnaireAttachment saves one to disk, for the table's
// 3-dot menu.
export const useDownloadVendorQuestionnaireAttachment = (): ((
  questionnaireRequestId: string,
  attachmentId: string,
) => Promise<boolean>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)

  return useCallback(
    async (questionnaireRequestId: string, attachmentId: string) => {
      try {
        const request = new DownloadVendorQuestionnaireAttachmentRequest()
          .setQuestionnaireRequestId(questionnaireRequestId)
          .setAttachmentId(attachmentId)
        const res = await client.downloadVendorQuestionnaireAttachment(request)
        FileSaver.saveAs(
          new Blob([res.getFile_asU8()], { type: res.getMimeType() }),
          res.getFileName(),
        )
        return true
      } catch (error) {
        log.error('Error downloading questionnaire attachment', error)
        showInfoToast('Could not download the attachment.')
        return false
      }
    },
    [client],
  )
}

export const useVendorQuestionnaireVersions = (
  questionnaireRequestId: string,
  shouldFetch = true,
): GrpcResponse<ListVendorQuestionnaireVersionsResponse> => {
  const req =
    new ListVendorQuestionnaireVersionsRequest().setQuestionnaireRequestId(
      questionnaireRequestId,
    )

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.listVendorQuestionnaireVersions,
    req,
    shouldFetch && !!questionnaireRequestId,
  )
  return NewGrpcResponse(response)
}

export const useVendorQuestionnaireVersion = (
  questionnaireRequestId: string,
  versionId: string,
  shouldFetch = true,
): GrpcResponse<GetVendorQuestionnaireVersionResponse> => {
  const req = new GetVendorQuestionnaireVersionRequest()
    .setQuestionnaireRequestId(questionnaireRequestId)
    .setVersionId(versionId)

  const { response } = useSwrImmutableGrpc(
    QuestionnaireServicePromiseClient.prototype.getVendorQuestionnaireVersion,
    req,
    shouldFetch && !!questionnaireRequestId && !!versionId,
  )
  return NewGrpcResponse(response)
}

export const useRemoveVendorQuestionnaire = (): ((
  questionnaireRequestId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(QuestionnaireServicePromiseClient)
  const mutate = useInvalidateVendorQuestionnaires()

  return useCallback(
    async (questionnaireRequestId: string) => {
      const req =
        new RemoveVendorQuestionnaireRequest().setQuestionnaireRequestId(
          questionnaireRequestId,
        )
      await client.removeVendorQuestionnaire(req)
      await mutate()
      showInfoToast('Questionnaire removed.')
    },
    [client, mutate],
  )
}

export interface VendorsColumnsVisibility {
  showName: boolean
  showRiskTier: boolean
  showStatus: boolean
  showState: boolean
  showEvaluation: boolean
  showAttestation: boolean
  showRelatedRisks: boolean
  showOwner: boolean
  showAssignees: boolean
  showLastReviewed: boolean
  showMenu: boolean
}

export const useVendorsColumnsVisibility = (): VendorsColumnsVisibility => {
  const isHidden = useScopedColumnHidden(INDEX_PAGE_WITH_FILTERS.VENDORS)
  // Gated like the vendor's Evaluations tab.
  const hasVendorEvaluations = useHasVendorTprmQuestionnaire()

  return {
    showName: true,
    showRiskTier: !isHidden('riskTier'),
    showStatus: !isHidden('status'),
    showState: !isHidden('state'),
    showEvaluation: hasVendorEvaluations && !isHidden('evaluation'),
    showAttestation: !isHidden('attestation'),
    showRelatedRisks: !isHidden('relatedRisks'),
    showOwner: !isHidden('owner'),
    showAssignees: !isHidden('assignees'),
    showLastReviewed: !isHidden('lastReviewed'),
    showMenu: true,
  }
}

export const getVendorsGridTemplateColumns = (
  visibility: VendorsColumnsVisibility,
): string => {
  const tracks: string[] = []
  if (visibility.showName) tracks.push('1fr')
  if (visibility.showRiskTier) tracks.push('max-content')
  if (visibility.showStatus) tracks.push('max-content')
  if (visibility.showState) tracks.push('max-content')
  if (visibility.showEvaluation) tracks.push('max-content')
  if (visibility.showAttestation) tracks.push('max-content')
  if (visibility.showRelatedRisks) tracks.push('max-content')
  if (visibility.showOwner) tracks.push('max-content')
  if (visibility.showAssignees) tracks.push('max-content')
  if (visibility.showLastReviewed) tracks.push('max-content')
  if (visibility.showMenu) tracks.push('max-content')
  return tracks.join(' ')
}
